import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, Twitter, Linkedin, Mail, Youtube, Film } from "lucide-react";

const FORMAT_LABELS = {
  twitter_thread: { label: "X / TWITTER THREAD", Icon: Twitter },
  linkedin_post: { label: "LINKEDIN POST", Icon: Linkedin },
  newsletter: { label: "NEWSLETTER", Icon: Mail },
  youtube_description: { label: "YT DESCRIPTION", Icon: Youtube },
  shorts_hooks: { label: "SHORTS HOOKS", Icon: Film },
};

const TONES = ["confident, editorial", "playful, witty", "raw, personal", "sharp, analytical"];

export default function Repurpose() {
  const [script, setScript] = useState("");
  const [tone, setTone] = useState(TONES[0]);
  const [formats, setFormats] = useState(Object.keys(FORMAT_LABELS));
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null);
  const [projects, setProjects] = useState([]);
  const [docs, setDocs] = useState([]);
  const [pickedProject, setPickedProject] = useState("");
  const [pickedDoc, setPickedDoc] = useState("");

  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(data)); }, []);
  useEffect(() => {
    if (!pickedProject) { setDocs([]); return; }
    api.get(`/projects/${pickedProject}/documents`).then(({ data }) => setDocs(data));
  }, [pickedProject]);

  const loadFromDoc = async () => {
    if (!pickedDoc) return;
    const { data } = await api.get(`/documents/${pickedDoc}`);
    setScript(data.content);
    toast.success(`Loaded "${data.title}"`);
  };

  const run = async () => {
    if (!script.trim()) return toast.error("Paste or load a script first");
    setLoading(true); setOutput(null);
    try {
      const { data } = await api.post("/repurpose", { script, tone, formats });
      setOutput(data);
      toast.success("Content generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repurposing failed");
    } finally { setLoading(false); }
  };

  const toggleFormat = (f) => setFormats((cur) => cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]);

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  return (
    <div className="min-h-screen">
      <div className="sticky top-0 z-30 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-[#27272a]">
        <div className="px-4 md:px-8 min-h-16 flex flex-wrap items-center gap-2 md:gap-3 py-3 md:py-0">
          <Sparkles size={16} strokeWidth={1.5} className="text-[#FF3B30]" />
          <div className="font-display text-xl">AI Repurpose Engine</div>
          <div className="sd-label ml-0 md:ml-2">CLAUDE SONNET 4.5</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-64px)]">
        {/* Left: source */}
        <div className="p-4 md:p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-[#27272a]">
          <div className="sd-label">ORIGINAL SCRIPT</div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <select value={pickedProject} onChange={(e) => setPickedProject(e.target.value)} className="sd-input h-9 max-w-[220px]" data-testid="repurpose-project-select">
              <option value="">Load from project…</option>
              {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.title}</option>)}
            </select>
            <select value={pickedDoc} onChange={(e) => setPickedDoc(e.target.value)} className="sd-input h-9 max-w-[260px]" disabled={!docs.length} data-testid="repurpose-doc-select">
              <option value="">Choose document…</option>
              {docs.map((d) => <option key={d.document_id} value={d.document_id}>{d.title}</option>)}
            </select>
            <button onClick={loadFromDoc} disabled={!pickedDoc} className="sd-btn sd-btn-ghost h-9" data-testid="repurpose-load-btn">Load</button>
          </div>

          <textarea
            value={script}
            onChange={(e) => setScript(e.target.value)}
            placeholder="Paste your finished script here, or load one from a project…"
            className="sd-textarea mt-4 min-h-[420px] leading-[1.7]"
            data-testid="repurpose-script-textarea"
          />

          <div className="mt-6 sd-label">TONE</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {TONES.map((t) => (
              <button
                key={t}
                onClick={() => setTone(t)}
                className={`px-3 h-8 border text-xs font-mono transition-colors ${tone === t ? "border-white bg-white text-black" : "border-[#27272a] text-zinc-400 hover:border-zinc-500"}`}
                data-testid={`tone-${t.split(",")[0]}`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="mt-6 sd-label">FORMATS</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.entries(FORMAT_LABELS).map(([k, v]) => {
              const on = formats.includes(k);
              return (
                <button
                  key={k}
                  onClick={() => toggleFormat(k)}
                  className={`px-3 h-8 border text-xs font-mono flex items-center gap-2 transition-colors ${on ? "border-white bg-white text-black" : "border-[#27272a] text-zinc-400 hover:border-zinc-500"}`}
                  data-testid={`format-${k}`}
                >
                  <v.Icon size={12} strokeWidth={1.5} /> {v.label}
                </button>
              );
            })}
          </div>

          <button
            onClick={run}
            disabled={loading || !script.trim() || formats.length === 0}
            className="sd-btn sd-btn-accent h-12 mt-8 w-full text-[15px]"
            data-testid="repurpose-generate-btn"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} strokeWidth={1.5} />}
            Repurpose with Claude Sonnet 4.5
          </button>
        </div>

        {/* Right: output */}
        <div className="p-4 md:p-8 lg:p-12 bg-[#0b0b0c]">
          <div className="sd-label">AI OUTPUT</div>
          {!output && !loading && (
            <div className="mt-12 border border-dashed border-[#27272a] p-10 text-center">
              <div className="font-display text-3xl">Waiting for your script.</div>
              <div className="mt-3 text-sm text-zinc-500 max-w-md mx-auto">
                One paste. Eight hooks. A thread. A newsletter. A description with timestamps.
                Everything you need to publish for a week.
              </div>
            </div>
          )}
          {loading && (
            <div className="mt-12 border border-[#27272a] p-10 text-center sd-tracing">
              <div className="sd-label mb-3">GENERATING</div>
              <div className="font-display text-2xl text-zinc-300">Claude is drafting your assets…</div>
            </div>
          )}

          {output && (
            <div className="mt-6 space-y-6">
              {output.twitter_thread && (
                <OutputCard title={FORMAT_LABELS.twitter_thread.label} Icon={FORMAT_LABELS.twitter_thread.Icon} onCopy={() => copy(output.twitter_thread.join("\n\n"))}>
                  <ol className="space-y-3">
                    {output.twitter_thread.map((t, i) => (
                      <li key={i} className="text-sm text-zinc-200 leading-relaxed border-l border-[#27272a] pl-3">
                        <span className="font-mono text-[10px] text-zinc-500 mr-2">{String(i + 1).padStart(2, "0")}</span>{t}
                      </li>
                    ))}
                  </ol>
                </OutputCard>
              )}
              {output.linkedin_post && (
                <OutputCard title={FORMAT_LABELS.linkedin_post.label} Icon={FORMAT_LABELS.linkedin_post.Icon} onCopy={() => copy(output.linkedin_post)}>
                  <pre className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap font-sans">{output.linkedin_post}</pre>
                </OutputCard>
              )}
              {output.newsletter && (
                <OutputCard
                  title={FORMAT_LABELS.newsletter.label}
                  Icon={FORMAT_LABELS.newsletter.Icon}
                  onCopy={() => copy(`Subject: ${output.newsletter.subject}\n\n${output.newsletter.body}`)}
                >
                  <div className="sd-label">SUBJECT</div>
                  <div className="text-white font-display text-xl mt-1">{output.newsletter.subject}</div>
                  <div className="sd-label mt-4">BODY</div>
                  <pre className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap font-sans mt-2">{output.newsletter.body}</pre>
                </OutputCard>
              )}
              {output.youtube_description && (
                <OutputCard title={FORMAT_LABELS.youtube_description.label} Icon={FORMAT_LABELS.youtube_description.Icon} onCopy={() => copy(output.youtube_description)}>
                  <pre className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap font-mono">{output.youtube_description}</pre>
                </OutputCard>
              )}
              {output.shorts_hooks && (
                <OutputCard title={FORMAT_LABELS.shorts_hooks.label} Icon={FORMAT_LABELS.shorts_hooks.Icon} onCopy={() => copy(output.shorts_hooks.join("\n"))}>
                  <ul className="grid md:grid-cols-2 gap-2">
                    {output.shorts_hooks.map((h, i) => (
                      <li key={i} className="border border-[#27272a] p-3 text-sm text-zinc-200 hover:border-zinc-500 transition-colors">
                        <span className="font-mono text-[10px] text-[#FF3B30] mr-2">H{i + 1}</span>{h}
                      </li>
                    ))}
                  </ul>
                </OutputCard>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OutputCard({ title, Icon, onCopy, children }) {
  return (
    <div className="sd-tracing pl-4 sd-rise">
      <div className="border border-[#27272a] bg-[#141414] p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Icon size={15} strokeWidth={1.5} className="text-[#FF3B30]" />
            <div className="sd-label">{title}</div>
          </div>
          <button onClick={onCopy} className="text-zinc-500 hover:text-white transition-colors" data-testid={`copy-${title.replace(/\s+/g, "-").toLowerCase()}`}>
            <Copy size={14} strokeWidth={1.5} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
